import React from 'react';
import { FadeIn } from './ui/FadeIn';
import { Button } from './ui/Button';
import { Compass, Gift, CheckCircle2, Search, ShieldAlert, FlaskConical, BadgeCheck, Leaf } from 'lucide-react';
export function BonusGuide() {
  const discoveries = [{
    icon: <FlaskConical size={20} data-id="element-30" />,
    text: 'Formas de minerales con mejor absorción'
  }, {
    icon: <ShieldAlert size={20} data-id="element-31" />,
    text: 'Ingredientes de relleno que conviene evitar'
  }, {
    icon: <Search size={20} data-id="element-32" />,
    text: 'Dosis engañosas y mezclas propietarias'
  }, {
    icon: <BadgeCheck size={20} data-id="element-33" />,
    text: 'Certificaciones que sí importan'
  }, {
    icon: <Leaf size={20} data-id="element-34" />,
    text: 'Suplementos derivados de frutas y verduras orgánicas que suelen ofrecer un enfoque más limpio y más alineado con la comida real'
  }];
  return <section className="py-24 px-4 bg-amber/5 relative overflow-hidden" data-id="element-35">
      {/* Decorative background */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-amber/10 rounded-full blur-3xl pointer-events-none -translate-y-1/2 translate-x-1/3" data-id="element-36" />

      <div className="max-w-4xl mx-auto relative z-10" data-id="element-37">
        <FadeIn data-id="element-38">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-39">
            <div className="bg-amber text-forest p-3 rounded-full shadow-md" data-id="element-40">
              <Gift size={28} data-id="element-41" />
            </div>
            <span className="bg-amber/20 text-amber-hover font-bold text-sm tracking-widest uppercase px-4 py-2 rounded-full" data-id="element-42">
              Bono Gratis Incluido
            </span>
          </div>
        </FadeIn>

        <FadeIn delay={0.1} data-id="element-43">
          <div className="text-center mb-12" data-id="element-44">
            <div className="inline-flex items-center gap-3 mb-4" data-id="element-45">
              <Compass className="text-forest" size={36} data-id="element-46" />
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-forest" data-id="element-47">
                La Brújula del Comprador
              </h2>
            </div>
            <p className="text-xl sm:text-2xl text-charcoal/70 font-medium" data-id="element-48">
              Cómo leer etiquetas de suplementos sin ser experto
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.2} data-id="element-49">
          <div className="bg-white rounded-3xl shadow-lg border border-sage/60 overflow-hidden" data-id="element-50">
            {/* Header banner */}
            <div className="bg-forest text-offwhite p-6 sm:p-8 text-center" data-id="element-51">
              <p className="text-lg sm:text-xl font-semibold mb-2" data-id="element-52">
                El filtro de 60 segundos para dejar de tirar tu dinero en
                "energía barata"
              </p>
              <p className="text-sage-dark text-sm" data-id="element-53">
                Tu cuerpo merece micronutrientes, no rellenos químicos.
              </p>
            </div>

            <div className="p-6 sm:p-10 space-y-8" data-id="element-54">
              {/* The problem */}
              <div data-id="element-55">
                <h3 className="font-serif text-xl font-bold text-forest mb-4" data-id="element-56">
                  ¿Has comprado suplementos y no sentiste diferencia?
                </h3>
                <p className="text-charcoal/80 leading-relaxed mb-4" data-id="element-57">
                  No siempre es porque "no funcionen". Muchas veces el problema
                  es que compraste una fórmula que se ve bien por fuera… pero es{' '}
                  <strong data-id="element-58">débil por dentro</strong>.
                </p>
                <p className="text-charcoal/80 leading-relaxed font-medium" data-id="element-59">
                  La realidad es simple:
                </p>
              </div>

              {/* Reality callout */}
              <div className="bg-amber/10 border-l-4 border-amber p-6 rounded-r-xl space-y-3" data-id="element-60">
                <p className="text-charcoal/80 leading-relaxed" data-id="element-61">
                  La mayoría de las etiquetas están hechas para{' '}
                  <strong data-id="element-62">vender</strong>, no para educarte.
                </p>
                <p className="text-charcoal/80 leading-relaxed" data-id="element-63">
                  Prometen energía, enfoque y vitalidad, pero esconden
                  ingredientes baratos, formas de baja absorción y mezclas poco
                  transparentes.
                </p>
                <p className="text-charcoal/90 leading-relaxed font-semibold" data-id="element-64">
                  Y si no sabes leer una etiqueta, es muy fácil gastar dinero en
                  suplementos que no están a la altura.
                </p>
              </div>

              {/* Solution */}
              <div data-id="element-65">
                <h3 className="font-serif text-xl font-bold text-forest mb-3" data-id="element-66">
                  Por eso creé La Brújula del Comprador
                </h3>
                <p className="text-charcoal/80 leading-relaxed" data-id="element-67">
                  Una guía práctica para ayudarte a evaluar suplementos en{' '}
                  <strong data-id="element-68">menos de 60 segundos</strong>, sin ser experto.
                </p>
              </div>

              {/* What you'll discover */}
              <div data-id="element-69">
                <h4 className="font-bold text-forest mb-5" data-id="element-70">
                  Dentro descubrirás cómo identificar:
                </h4>
                <div className="space-y-4" data-id="element-71">
                  {discoveries.map((item, i) => <div key={i} className="flex items-start gap-4" data-id="element-72">
                      <div className="w-10 h-10 bg-forest/10 rounded-lg flex items-center justify-center shrink-0 text-forest mt-0.5" data-id="element-73">
                        {item.icon}
                      </div>
                      <p className="text-charcoal/80 leading-relaxed" data-id="element-74">
                        {item.text}
                      </p>
                    </div>)}
                </div>
              </div>

              {/* Benefits */}
              <div className="bg-sage/40 rounded-2xl p-6 sm:p-8" data-id="element-75">
                <h3 className="font-serif text-xl font-bold text-forest mb-4" data-id="element-76">
                  Esta guía te ayuda a comprar con más criterio
                </h3>
                <div className="space-y-3" data-id="element-77">
                  {['Ya no para dejarte llevar por un envase bonito.', 'Ya no para asumir que "más caro" significa "mejor".', 'Ya no para meter en tu cuerpo algo que no entiendes.'].map((text, i) => <div key={i} className="flex items-start gap-3" data-id="element-78">
                      <CheckCircle2 className="text-forest shrink-0 mt-0.5" size={20} data-id="element-79" />
                      <p className="text-charcoal/80" data-id="element-80">{text}</p>
                    </div>)}
                </div>
                <p className="text-charcoal/80 leading-relaxed mt-4 font-medium" data-id="element-81">
                  Con esta guía, aprendes a mirar detrás del marketing y a
                  elegir fórmulas con más claridad y confianza.
                </p>
              </div>

              {/* Personalized help */}
              <div className="border border-forest/20 rounded-2xl p-6 text-center" data-id="element-82">
                <h4 className="font-bold text-forest mb-2" data-id="element-83">
                  ¿Y si quieres ayuda personalizada?
                </h4>
                <p className="text-charcoal/70 leading-relaxed text-sm" data-id="element-84">
                  Contáctame y con gusto puedo orientarte con recomendaciones de
                  multivitaminas y minerales de acuerdo con esta guía y
                  adaptadas a tus necesidades personales. Solo escríbeme y
                  cuéntame un poco sobre tu rutina, tu energía y lo que estás
                  buscando.
                </p>
              </div>

              {/* CTA */}
              <div className="text-center pt-4" data-id="element-85">
                <p className="font-serif text-2xl font-bold text-forest mb-2" data-id="element-86">
                  Obtén ahora La Brújula del Comprador
                </p>
                <p className="text-charcoal/70 mb-8" data-id="element-87">
                  Deja de comprar promesas.{' '}
                  <strong data-id="element-88">Empieza a comprar fórmulas.</strong>
                </p>
                <Button href="#pricing" data-id="element-89">¡QUIERO MI GUÍA AHORA!</Button>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}